'use client';

import { c as _c } from "react/compiler-runtime";
import { BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, Breadcrumb as BreadcrumbPrimitive, BreadcrumbSeparator } from '@achra/ui/breadcrumb';
import { cn } from '@achra/ui/lib/utils';
import Link from 'next/link';
import { Fragment, Suspense, useMemo, useRef, useState } from 'react';
import { useMountedState } from 'react-use';
import { useResizeObserver } from 'usehooks-ts';
import { getTextWidth, mobileRecommendedSegmentWidth } from '@/modules/shared/lib/breadcrumb-utils';
import { MAX_ALLOWED_WIDTH, MAX_SEGMENT_WIDTH_MOBILE_DEFAULT, THREE_DOTS_WIDTH } from '@/modules/shared/lib/constants';
import type { BreadcrumbItemExtended, BreadcrumbItemNavigation } from '@/modules/shared/types/breadcrumb';
import { DotsSegment } from './components/dot-segments';
import { EllipsisSkeleton } from './components/ellipsis-skeleton';
import type { Route } from 'next';
interface BreadcrumbProps {
  items: BreadcrumbItemNavigation[];
  defaultOpen?: boolean;
  className?: string;
  maxSegmentWidthMobile?: number;
}
function Breadcrumb(t0) {
  const $ = _c(51);
  const {
    items,
    defaultOpen: t1,
    className,
    maxSegmentWidthMobile: t2
  } = t0;
  const defaultOpen = t1 === undefined ? false : t1;
  const maxSegmentWidthMobile = t2 === undefined ? MAX_SEGMENT_WIDTH_MOBILE_DEFAULT : t2;
  const isMounted = useMountedState();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [0, 0];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [elementWidths, setElementWidths] = useState(t3);
  const contentRef = useRef(null);
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      ref: contentRef as React.RefObject<HTMLElement>,
      onResize: size => {
        if (size.width) {
          setElementWidths([size.width]);
        }
      }
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  useResizeObserver(t4);
  let t5;
  if ($[2] !== isMounted || $[3] !== items) {
    let t6;
    if ($[5] !== isMounted || $[6] !== items.length) {
      t6 = (item, index) => {
        const isLastItem = index === items.length - 1;
        const labelWidth = !isMounted() ? MAX_ALLOWED_WIDTH : getTextWidth(item.label, `${isLastItem ? 400 : 600} 16px Inter`);
        return {
          ...item,
          labelWidth,
          recommendedWidth: isLastItem ? labelWidth : Math.min(labelWidth, MAX_ALLOWED_WIDTH)
        };
      };
      $[5] = isMounted;
      $[6] = items.length;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    t5 = items.map(t6);
    $[2] = isMounted;
    $[3] = items;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const itemsExtended = t5;
  let t6;
  bb0: {
    if (itemsExtended.length <= 3) {
      t6 = itemsExtended;
      break bb0;
    }
    const t7 = itemsExtended[0];
    let t8;
    if ($[8] !== itemsExtended) {
      t8 = itemsExtended.slice(1, itemsExtended.length - 2);
      $[8] = itemsExtended;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    let t9;
    if ($[10] !== t8) {
      t9 = {
        label: "...",
        href: "#" as Route,
        labelWidth: 0,
        recommendedWidth: THREE_DOTS_WIDTH,
        attachedItems: t8
      };
      $[10] = t8;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    let t10;
    if ($[12] !== itemsExtended || $[13] !== t9) {
      t10 = [t7, t9, ...itemsExtended.slice(-2)];
      $[12] = itemsExtended;
      $[13] = t9;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    t6 = t10;
  }
  const groupedItems = t6;
  let t7;
  bb1: {
    if (!isMounted() || groupedItems.length === 0) {
      t7 = undefined;
      break bb1;
    }
    const lastItem = groupedItems[groupedItems.length - 1];
    t7 = lastItem.labelWidth > MAX_ALLOWED_WIDTH ? MAX_ALLOWED_WIDTH : undefined;
  }
  const lastItemMaxWidth = t7;
  let t8;
  if ($[15] !== className) {
    t8 = cn("w-full", className);
    $[15] = className;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== defaultOpen || $[18] !== items || $[19] !== itemsExtended.length) {
    t9 = itemsExtended.length > 1 && <><BreadcrumbItem><Suspense fallback={<EllipsisSkeleton />}><DotsSegment items={items} defaultOpen={defaultOpen} /></Suspense></BreadcrumbItem><BreadcrumbSeparator className="text-foreground/30 [&>svg]:size-6" /></>;
    $[17] = defaultOpen;
    $[18] = items;
    $[19] = itemsExtended.length;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== elementWidths || $[22] !== isMounted || $[23] !== maxSegmentWidthMobile) {
    t10 = mobileRecommendedSegmentWidth(isMounted(), elementWidths) ?? maxSegmentWidthMobile;
    $[21] = elementWidths;
    $[22] = isMounted;
    $[23] = maxSegmentWidthMobile;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = {
      maxWidth: t10
    };
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = itemsExtended[itemsExtended.length - 1]?.label;
  let t13;
  if ($[27] !== t12) {
    t13 = <BreadcrumbPage className="overflow-hidden text-base font-semibold text-ellipsis whitespace-nowrap">{t12}</BreadcrumbPage>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13) {
    t14 = <BreadcrumbItem style={t11}>{t13}</BreadcrumbItem>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14 || $[33] !== t9) {
    t15 = <div className="flex w-full items-center lg:hidden"><BreadcrumbList>{t9}{t14}</BreadcrumbList></div>;
    $[32] = t14;
    $[33] = t9;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== defaultOpen || $[36] !== groupedItems || $[37] !== lastItemMaxWidth) {
    let t17;
    if ($[39] !== defaultOpen || $[40] !== groupedItems.length || $[41] !== lastItemMaxWidth) {
      t17 = (item_0, index_0) => {
        const isLastItem_0 = index_0 === groupedItems.length - 1;
        return <Fragment key={`${item_0.label}-${item_0.href}-${item_0.recommendedWidth}`}><BreadcrumbItem className={cn("group text-foreground/30 cursor-pointer text-base font-normal", isLastItem_0 && "min-w-0", isLastItem_0 && lastItemMaxWidth === undefined && "flex-1")} style={isLastItem_0 ? lastItemMaxWidth !== undefined ? {
            maxWidth: `${lastItemMaxWidth}px`,
            width: `${lastItemMaxWidth}px`
          } : undefined : {
            maxWidth: item_0.recommendedWidth
          }}>{item_0.label === "..." ? <Suspense fallback={<EllipsisSkeleton />}><DotsSegment items={item_0.attachedItems ?? []} defaultOpen={defaultOpen} /></Suspense> : isLastItem_0 ? <BreadcrumbPage className="block w-full min-w-0 overflow-hidden text-base font-semibold text-ellipsis whitespace-nowrap">{item_0.label}</BreadcrumbPage> : <BreadcrumbLink asChild={true}><Link href={item_0.href} className="text-foreground/50 flex items-center overflow-hidden font-normal text-ellipsis whitespace-nowrap no-underline"><span className="cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap">{item_0.label}</span></Link></BreadcrumbLink>}</BreadcrumbItem>{index_0 < groupedItems.length - 1 && <BreadcrumbSeparator className="text-foreground/30 pr-1 [&>svg]:size-6" />}</Fragment>;
      };
      $[39] = defaultOpen;
      $[40] = groupedItems.length;
      $[41] = lastItemMaxWidth;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    t16 = groupedItems.map(t17);
    $[35] = defaultOpen;
    $[36] = groupedItems;
    $[37] = lastItemMaxWidth;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[43] !== t16) {
    t17 = <div className="hidden w-full items-center lg:flex"><BreadcrumbList>{t16}</BreadcrumbList></div>;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t15 || $[46] !== t17) {
    t18 = <div className="w-[calc(100%-32px)] max-md:mx-4 max-md:rounded-xl md:w-full"><div><div ref={contentRef} className="flex items-center justify-between max-md:-mx-2">{t15}{t17}</div></div></div>;
    $[45] = t15;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t18 || $[49] !== t8) {
    t19 = <BreadcrumbPrimitive className={t8}>{t18}</BreadcrumbPrimitive>;
    $[48] = t18;
    $[49] = t8;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
}
export { Breadcrumb };